import type { OrgConfigMetadata } from "@rubi/api-generated";

/**
 * Validation for manually-entered RMS incident / occurrence numbers.
 *
 * There are two org-config sources, and they answer different questions:
 *
 *   `incident_number_regex`     what the agency ACCEPTS. Authoritative.
 *   `incident_number_pattern`   what the generator EMITS, plus
 *   `incident_number_length`    the digit width of its counter.
 *
 * Historically only the generator pair existed, so acceptance was *inferred*
 * from it — fine for agencies whose officers only ever type numbers the
 * generator could have produced, wrong for agencies whose RMS also holds
 * legacy or externally-issued numbers of a different shape. `incident_number_regex`
 * states the accepted shape outright; when it is set, the generator pair is
 * ignored for validation (it still drives generation and the worked example).
 *
 * Generator tokens — everything else in the pattern is a literal:
 *   {tracking_number}  the zero-padded incrementing sequence
 *   {YYYY}             4-digit year
 *   {YY}               2-digit year
 *
 * The inferred regex mirrors `init_incident_number` in api-rs
 * `report/submission.rs` exactly: whatever the backend would produce must
 * always pass validation here.
 *
 * NOTE: the generator's supported tokens are NOT the `{yyyy}/{mm}/{seq}` set
 * described in DevOrgConfigPage's help text — that help text is wrong.
 */

/** Matched in a single pass so `{YY}` can never be substituted inside `{YYYY}`. */
const TOKEN_PATTERN = /\{tracking_number\}|\{YYYY\}|\{YY\}/g;

const escapeRegex = (literal: string) =>
  literal.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export interface IncidentNumberFormat {
  /** Explicit accepted-value regex from org config, e.g. `\d{4}-\d{6}`. */
  regex?: string;
  /** Raw generator template from org config, e.g. `{YYYY}-{tracking_number}`. */
  pattern?: string;
  /** Digit width of the sequence portion. */
  length?: number;
}

/**
 * Reads the configured format off org-config metadata, or `null` when the
 * agency has specified neither an explicit regex nor a complete generator pair
 * (in which case any input is accepted).
 */
export function getIncidentNumberFormat(
  metadata: Partial<OrgConfigMetadata> | null | undefined,
): IncidentNumberFormat | null {
  const regex = metadata?.incident_number_regex?.trim();
  const pattern = metadata?.incident_number_pattern;
  const length = metadata?.incident_number_length;
  const hasGenerator = !!pattern && length != null && length > 0;

  if (!regex && !hasGenerator) return null;

  return {
    ...(regex ? { regex } : {}),
    ...(hasGenerator ? { pattern, length } : {}),
  };
}

/**
 * Anchors the agency's regex so it has to describe the whole value.
 *
 * Wrapping rather than testing for existing anchors keeps a hand-written
 * `^AB\d{6}$` and a bare `AB\d{6}` equivalent, and stops an alternation
 * (`A\d{4}|B\d{4}`) from silently accepting a suffix match. Returns `null` for
 * a regex that does not compile — a bad config must not break the field.
 */
const compileExplicitRegex = (regex: string): RegExp | null => {
  try {
    return new RegExp(`^(?:${regex})$`);
  } catch {
    console.warn(`Ignoring invalid incident_number_regex: ${regex}`);
    return null;
  }
};

/**
 * Compiles the generator pattern into an anchored regex.
 *
 * The sequence is an exact `\d{length}`. Real patterns run the tokens together
 * with no separator (`BR{YY}{tracking_number}`), so the digit count is the only
 * thing pinning the overall length — an open-ended `\d{length,}` would collapse
 * to "prefix + any 8-or-more digits" and let typos through.
 *
 * Trade-off: the generator zero-pads to a *minimum* width (`{:0w$}`), so a
 * counter that outgrows `length` (>999,999 records at length 6) emits an extra
 * digit that this rejects. That number would already violate the agency's own
 * RMS format, so surfacing it is better than silently widening the check — and
 * an agency that wants the slack can now say so in `incident_number_regex`.
 */
const compileGeneratorRegex = (
  pattern: string,
  length: number,
): RegExp | null => {
  let source = "";
  let lastIndex = 0;

  TOKEN_PATTERN.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = TOKEN_PATTERN.exec(pattern)) !== null) {
    source += escapeRegex(pattern.slice(lastIndex, match.index));
    if (match[0] === "{tracking_number}") {
      source += `\\d{${length}}`;
    } else if (match[0] === "{YYYY}") {
      source += "\\d{4}";
    } else {
      source += "\\d{2}";
    }
    lastIndex = match.index + match[0].length;
  }
  source += escapeRegex(pattern.slice(lastIndex));

  return new RegExp(`^${source}$`);
};

/**
 * The regex a value must satisfy, or `null` when the format carries nothing
 * usable — an explicit regex that will not compile with no generator pair to
 * fall back on, which is treated as "accept anything" rather than "reject
 * everything".
 */
export function buildIncidentNumberRegex(
  format: IncidentNumberFormat,
): RegExp | null {
  if (format.regex) {
    const explicit = compileExplicitRegex(format.regex);
    if (explicit) return explicit;
  }

  if (format.pattern && format.length != null && format.length > 0) {
    return compileGeneratorRegex(format.pattern, format.length);
  }

  return null;
}

/**
 * Renders the generator pattern with representative values, so officers see a
 * concrete example (`2026-000001`) instead of the raw template.
 *
 * `null` when there is no example worth showing: no generator pair, or one
 * whose output the accepted-value regex would reject. An agency can legitimately
 * accept a shape its generator never emits, but an example the field then
 * rejects is worse than no example at all.
 *
 * A caller that has already compiled the format passes it as `accepted` so the
 * regex isn't built — and a bad one re-logged — a second time. `null` means
 * "compiled, nothing usable"; omitting it compiles on demand.
 */
export function describeIncidentNumberFormat(
  format: IncidentNumberFormat,
  accepted?: RegExp | null,
): string | null {
  const { pattern, length } = format;
  if (!pattern || length == null || length <= 0) return null;

  // Generation is UTC-based in the backend; match it so the example agrees.
  const year = String(new Date().getUTCFullYear());

  const example = pattern
    .replace(/\{YYYY\}/g, year)
    .replace(/\{YY\}/g, year.slice(-2))
    .replace(/\{tracking_number\}/g, "1".padStart(length, "0"));

  // Resolved after the guard above, so a regex-only format still returns
  // without compiling anything.
  const regex =
    accepted === undefined ? buildIncidentNumberRegex(format) : accepted;
  return !regex || regex.test(example) ? example : null;
}

/**
 * Validates a manually-entered incident number against the agency format.
 * Returns an error message, or `null` when the value is acceptable.
 *
 * A blank value is always valid — it means "auto-generate" (or the officer
 * clearing the field), which the existing helper text already promises.
 * Matching is case-sensitive: the value has to be usable as the real RMS
 * record number, and the generator emits pattern literals verbatim.
 */
export function validateIncidentNumber(
  value: string,
  format: IncidentNumberFormat | null,
): string | null {
  if (!format) return null;

  const trimmed = value.trim();
  if (!trimmed) return null;

  const regex = buildIncidentNumberRegex(format);
  if (!regex || regex.test(trimmed)) return null;

  const example = describeIncidentNumberFormat(format, regex);

  return example
    ? `Does not match your agency's incident number format (e.g. ${example})`
    : "Does not match your agency's incident number format";
}
